import dynamic from 'next/dynamic';
import { memo, useMemo, useState } from 'react';
import Download from '~/icons/iconography/download.svg';
import { useAccountInfo } from '~/services/shop/hooks';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { DatePickerRange } from '~/shared/components/DatePicker';
import SearchField from '~/shared/components/SearchField';
import { SkeletonLoader } from '~/shared/components/SkeletonLoader';
import { Text } from '~/shared/components/Text';
import { CurrentFilters } from '../../shared/CurrentFilters/CurrentFilters';
import { DropdownSelector } from '../../shared/DropdownSelector';
import { ModuleContainer } from '../../shared/ModuleContainer';
import Table from './components/Table';
import useCsvExport from './hooks/useCsvExport';
import { useFilterTableData } from './hooks/useFilterTableData';
import {
    DownloadDocuments,
    Filter,
    FilterWrapper,
    Footer,
    Header,
    PageSetter,
    Pagination,
    SearchContainer,
    Section,
    SkeletonSpacer,
    TableContainer,
} from './styles';

const AccountTable = memo(Table);
const SelectorMemo = memo(DropdownSelector);

const CSVLoader = dynamic(() => import('react-csv-downloader'), { ssr: false });

export default function M202AccountInformation() {
    const { data, isLoading } = useAccountInfo();
    const { translate } = useTranslations();

    const [dateRange, setDateRange] = useState<(Date | null)[]>([null, null]);

    const { filteredData, setQuery, setSelectedStatus, query, selectedStatus } = useFilterTableData(
        data ?? [],
        dateRange,
    );
    const { columnsCsv, csvData } = useCsvExport(filteredData ?? []);

    const options = useMemo(
        () => [
            {
                label: translate('mit-fog.accountInfo.openItems'),
                value: translate('mit-fog.accountInfo.openItems'),
            },
            {
                label: translate('mit-fog.accountInfo.balanced'),
                value: translate('mit-fog.accountInfo.balanced'),
            },
            {
                label: translate('mit-fog.accountInfo.invoice'),
                value: translate('mit-fog.accountInfo.invoice'),
            },
            {
                label: translate('mit-fog.accountInfo.proformaInvoice'),
                value: translate('mit-fog.accountInfo.proformaInvoice'),
            },

            {
                label: translate('mit-fog.accountInfo.creditNote'),
                value: translate('mit-fog.accountInfo.creditNote'),
            },

            {
                label: translate('mit-fog.accountInfo.payment'),
                value: translate('mit-fog.accountInfo.payment'),
            },
            {
                label: translate('mit-fog.accountInfo.reminder'),
                value: translate('mit-fog.accountInfo.reminder'),
            },
            {
                label: translate('mit-fog.accountInfo.interest'),
                value: translate('mit-fog.accountInfo.interest'),
            },
        ],
        [translate],
    );

    if (isLoading || !data) return <Skeleton />;

    return (
        <ModuleContainer spacingTop="none">
            <Section>
                <Text as="h2" variant="display2">
                    {translate('mit-fog.orders.kontoInformation')} ({filteredData?.length})
                </Text>
                <Header>
                    <SearchContainer>
                        <SearchField
                            type="text"
                            value={query}
                            setInputValue={setQuery}
                            placeholder={translate('mit-fog.searchInvoiceOrderNumber')}
                        />
                    </SearchContainer>

                    <Filter>
                        <SelectorMemo
                            id="status"
                            label="Status"
                            selectedItems={selectedStatus}
                            setSelectedItems={setSelectedStatus}
                            options={options}
                        />
                    </Filter>

                    <DatePickerRange
                        setDateRange={setDateRange}
                        onChange={(dates) => (dates ? setDateRange(dates) : null)}
                        selectsRange
                        startDate={dateRange[0]}
                        endDate={dateRange[1]}
                    />

                    <DownloadDocuments>
                        <CSVLoader
                            filename="fog.csv"
                            extension=".csv"
                            datas={csvData}
                            columns={columnsCsv}
                            separator=";"
                            text={translate('mit-fog.order.downloadCsv')}
                        >
                            <Button variant="fill" customIcon={<Download />}>
                                {translate('mit-fog.order.downloadCsv')}
                            </Button>
                        </CSVLoader>
                    </DownloadDocuments>
                </Header>
                {selectedStatus.length > 0 && (
                    <FilterWrapper>
                        <CurrentFilters
                            filterElementList={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                        />
                    </FilterWrapper>
                )}
                <AccountTable data={filteredData} />
            </Section>
        </ModuleContainer>
    );
}

function Skeleton() {
    return (
        <ModuleContainer spacingTop="none" fullWidth>
            <Section>
                <SkeletonLoader width={300} height={38} />
                <Header>
                    <SearchContainer>
                        <SkeletonLoader height={40} isFullWidth />
                    </SearchContainer>
                    <Filter>
                        <SkeletonLoader height={40} isFullWidth />
                    </Filter>
                    <SkeletonLoader height={40} width={210} />
                    <DownloadDocuments>
                        <SkeletonLoader height={40} isFullWidth />
                    </DownloadDocuments>
                </Header>
                <TableContainer>
                    {Array.from({ length: 11 }).map((_, index) => (
                        <div key={index}>
                            <SkeletonLoader isFullWidth height={48} />
                            <SkeletonSpacer />
                        </div>
                    ))}
                </TableContainer>
                <Footer>
                    <PageSetter>
                        <SkeletonLoader height={40} width={150} />
                    </PageSetter>
                    <Pagination>
                        <SkeletonLoader height={40} width={40} />
                        <SkeletonLoader height={40} width={150} />
                        <SkeletonLoader height={40} width={40} />
                    </Pagination>
                </Footer>
            </Section>
        </ModuleContainer>
    );
}
